import CONFIG from '@/constants/config.js';
import { groupEvents, visibleRows } from '@/utils/event-list.mjs';

/**
 * Onboarding flags and the landing decision after app start / login (mirrors the native
 * LoginActivity / Splash order: QR, single-event auto checkin, selection card, main event, list).
 * Host components must also use settingsMixin, layoutMixin, eventMixin and qrLoginMixin.
 */
export var onboardingMixin = {
    computed: {
        easyEventMode() {
            return CONFIG.EASY_EVENT_SELECT;
        },
        easyEventForceEventList() {
            return this.parseBoolean(CONFIG.EASY_EVENT_FORCE_EVENT_LIST, false);
        },
        easyEventHideMain() {
            return this.parseBoolean(CONFIG.EASY_EVENT_HIDE_MAIN, false);
        },
        easyEventHideScan() {
            return this.parseBoolean(CONFIG.EASY_EVENT_HIDE_SCAN, false);
        },
        hideMainEvent() {
            return this.parseBoolean(CONFIG.HIDE_MAIN_EVENT, false);
        },
        hideInactiveEvents() {
            return this.parseBoolean(CONFIG.EVENTLIST_HIDE_INACTIVE, false);
        },
        quizEventsEnabled() {
            return this.parseBoolean(CONFIG.EVENTLIST_QUIZ_EVENTS, false);
        },
        // game types of the event list; the native apps only load the location based game
        availableGametypes() {
            return this.quizEventsEnabled ? [CONFIG.GAMETYPE_LBG, CONFIG.GAMETYPE_QUIZ] : [CONFIG.GAMETYPE_LBG];
        },
        autoCheckinSingleEvent() {
            return this.parseBoolean(CONFIG.EVENTLIST_AUTO_CHECKIN_SINGLE, false);
        },
        disableQRAccess() {
            return this.parseBoolean(CONFIG.DISABLE_QR_ACCESS, false);
        },
        disablePINAccess() {
            return this.parseBoolean(CONFIG.DISABLE_PIN_ACCESS, false);
        },
        disableLogout() {
            return this.parseBoolean(CONFIG.DISABLE_LOGOUT, false);
        },
        // the selection card is in play: forced by env, or server setting easy_event_selection in auto mode
        cardEffective() {
            if (this.easyEventMode === CONFIG.EASY_EVENT_MODE_FORCE) {
                return true;
            }
            return this.easyEventMode === CONFIG.EASY_EVENT_MODE_AUTO && this.serverEasyEventSelection;
        },
        mainAvailable() {
            return this.mainEventId > 0 && !this.hideMainEvent;
        },
        cardShowsMainEvent() {
            return this.mainAvailable && !this.easyEventHideMain;
        },
        cardShowsScan() {
            return !this.easyEventHideScan && (!this.disableQRAccess || !this.disablePINAccess);
        },
        // a card with only the list button is skipped in favour of the list itself
        cardHasContent() {
            return this.cardShowsMainEvent || this.cardShowsScan;
        },
    },
    methods: {
        // after login/guest login: a QR in the URL wins, otherwise refresh the app settings and land
        landAfterLogin() {
            if (this.hasQRData()) {
                this.goToEventList({ d: this.getQrData(), e: this.getQrEventId() }, true);
                return Promise.resolve();
            }
            return this.loadAppSettings().then(() => this.land());
        },
        // app start without QR: resume the current event, otherwise land
        landOnStart() {
            if (null != this.selectedEventId) {
                this.goToEvent(this.selectedEventId, true);
                return Promise.resolve();
            }
            return this.land();
        },
        land() {
            return this.findSingleEventForAutoCheckin().then((event) => {
                if (event) {
                    this.goToEvent(event.id_event, true);
                    return;
                }
                if (this.cardEffective) {
                    if (this.cardHasContent && !this.easyEventForceEventList) {
                        this.goToEventSelect(true);
                    } else {
                        this.goToEventList(undefined, true);
                    }
                    return;
                }
                if (
                    this.easyEventMode === CONFIG.EASY_EVENT_MODE_AUTO &&
                    !this.serverEasyEventSelection &&
                    this.mainAvailable
                ) {
                    this.goToEvent(this.mainEventId, true);
                    return;
                }
                this.goToEventList(undefined, true);
            });
        },
        // legacy auto checkin: exactly one visible row (after the list filters) and that row is active
        findSingleEventForAutoCheckin() {
            if (!this.autoCheckinSingleEvent) {
                return Promise.resolve(null);
            }
            return this.getEvents(this.availableGametypes)
                .then((events) => {
                    const groups = groupEvents(events, {
                        nowMs: Date.now(),
                        parseInTz: this.parseInEventTimezone,
                        hideMain: this.hideMainEvent,
                        hideInactive: this.hideInactiveEvents,
                    });
                    const active = groups.my.active.concat(groups.public.active);
                    return visibleRows(groups).length === 1 && active.length === 1 ? active[0] : null;
                })
                .catch(() => null);
        },
        // where a logged-in user goes after a failed checkin; never auto-joins and never opens /login while logged in
        goToAccessibleArea() {
            if (!this.isLoggedIn) {
                this.goToLogin();
                return;
            }
            if (this.cardEffective && this.cardHasContent) {
                this.goToEventSelect();
            } else {
                this.goToEventList();
            }
        },
    },
};
